import { useState } from "react";
import { useTranslations } from "use-intl";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import type { AccountType } from "@/types/accounts";

interface AccountTypeCardsProps {
  bankName: string;
  onContinue: (accountType: AccountType) => void;
}

/**
 * FIN-1567 (AC1-AC4): the consultant-app equivalent of packages/ui's AccountTypeChoice. Built
 * locally rather than importing that component -- src/ does not yet consume
 * @financialharmony/ui's React components (packages/ui/CLAUDE.md's migration roadmap; see also
 * src/hooks/useArrivalTimeout.ts's own note on the same boundary), so this mirrors its behaviour
 * (neither card preselected, Continue disabled until one is picked -- AC2) with src/'s own
 * shadcn primitives and token set instead.
 */
export function AccountTypeCards({ bankName, onContinue }: AccountTypeCardsProps) {
  const t = useTranslations("consultantBank.connect");
  const [value, setValue] = useState<AccountType | undefined>(undefined);

  return (
    <div className="space-y-4 py-4">
      <div>
        <h3 className="text-base font-medium">{t("accountType.title", { bank: bankName })}</h3>
        <p className="text-sm text-muted-foreground">
          {t("accountType.hint")}
        </p>
      </div>

      <RadioGroup value={value} onValueChange={(next) => setValue(next as AccountType)} className="gap-3">
        <Label
          htmlFor="account-type-business"
          className="flex items-start gap-3 rounded-lg border p-4 font-normal cursor-pointer"
          data-testid="account-type-card-business"
        >
          <RadioGroupItem value="business" id="account-type-business" className="mt-1" />
          <span className="flex-1 space-y-1">
            <span className="block font-medium">{t("accountType.business")}</span>
            <span className="block text-sm text-muted-foreground">{t("accountType.businessBody")}</span>
          </span>
        </Label>
        <Label
          htmlFor="account-type-personal"
          className="flex items-start gap-3 rounded-lg border p-4 font-normal cursor-pointer"
          data-testid="account-type-card-personal"
        >
          <RadioGroupItem value="personal" id="account-type-personal" className="mt-1" />
          <span className="flex-1 space-y-1">
            <span className="block font-medium">{t("accountType.personal")}</span>
            <span className="block text-sm text-muted-foreground">{t("accountType.personalBody")}</span>
          </span>
        </Label>
      </RadioGroup>

      <Button
        className="w-full"
        disabled={!value}
        onClick={() => value && onContinue(value)}
        data-testid="account-type-choice-continue"
      >
        {t("continue")}
      </Button>
    </div>
  );
}
